import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import Modal from '../common/Modal'
import { CompanyServiceName, DisplayNameDetail, RequestPatchTaskCompanyServiceName } from '../../../states/types/task'
import AddIconModule from './AddIconModule'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { compile } from '../../../utils/search'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'
import { nowDate } from '../../../utils/date'
import LoadingSquare from '../common/LoadingSquare'

interface Props {
  appList: DisplayNameDetail[]
  details: DisplayNameDetail[]
  closeCallback: VoidFunction
  modCallback: (params: RequestPatchTaskCompanyServiceName) => void
  isDisplay: boolean
  windowTitles: string[]
}

const SLICE_LIMIT = 10

const EditTargetApplicationModal: FC<Props> = (props) => {
  const {
    appList,
    details,
    closeCallback,
    modCallback,
    isDisplay,
    windowTitles,
  } = props

  const [detailData, setDetailData] = useState<DisplayNameDetail[]>([])

  const [displayName, setDisplayName] = useState<string>("")
  const [displayColor, setDisplayColor] = useState<string>("")
  const [displayIcon, setDisplayIcon] = useState<string>("")

  const [suggestList, setSuggestList] = useState<DisplayNameDetail[]>([])
  const [isDuplicateApp, setIsDuplicateApp] = useState<boolean>(false)

  const [sliceNumber, setSliceNumber] = useState<number>(0)

  const suggestRef = useRef<HTMLDivElement>(null)

  const slicedData = useMemo(() => {
    const length = Math.ceil(suggestList.length / SLICE_LIMIT)
    return new Array(length).fill(null).map((_, i) => suggestList.slice(i * SLICE_LIMIT, (i + 1) * SLICE_LIMIT))
  }, [suggestList])

  const suggestHeight = () => suggestRef.current?.scrollHeight || 0

  const onScroll = () => {
    if (((suggestRef.current?.scrollTop || 0)) > suggestHeight() - 220) {
      setSliceNumber((self) => {
        if (slicedData.length <= self) return self
        return self + 1
      })
    }
  }

  const resetParam = () => {
    setDisplayName("")
    setDisplayColor("")
    setDisplayIcon("")
    setSuggestList([])
    setIsDuplicateApp(false)
  }

  const closeModal = () => {
    closeCallback()
    resetParam()
  }

  const submitTargetApplication = () => {
    const params: RequestPatchTaskCompanyServiceName = {
      company_service_name_ids: detailData.map((v) => v.company_service_names.map((w) => w.id)).flat(),
      service_name: displayName,
      color: displayColor,
      icon: displayIcon,
    }
    modCallback(params)
    closeModal()
  }

  const separateTargetApplication = (target: CompanyServiceName) => {
    const serviceName = appList.map((v) => v.display_name).includes(target.raw_name)
      ? `${target.raw_name}_${nowDate("YYYYMMDDHHmmss")}`
      : target.raw_name
    setDetailData(detailData.map((v) => ({
      ...v,
      display_name: serviceName,
      color: "#333333",
      icon: "fas fa-desktop",
      company_service_names: v.company_service_names.filter((w) => w.id === target.id),
    })))
  }

  const search = (words: string) => {
    if (!words) {
      setSuggestList([])
      return
    }
    const word = words.toLowerCase().split(/\s+/)
    const tmpData = appList.filter((value) => {
      if (value.display_name === null) return false
      return (compile(word).test(value.display_name.toLocaleLowerCase()))
    })
    setSuggestList(tmpData)
    setSliceNumber(0)
  }

  const setName = (name: string) => {
    setDisplayName(name)
    search(name)
    const displayNames = detailData.map((v) => v.display_name)
    const duplicateApp = appList.filter((v) => !displayNames.includes(v.display_name)).find((v) => v.display_name === name)
    if (duplicateApp) {
      setDisplayIcon(duplicateApp.icon)
      setDisplayColor(duplicateApp.color)
      setIsDuplicateApp(true)
      return
    }
    setIsDuplicateApp(false)
  }

  const selectSuggest = (name: string) => {
    setName(name)
    setSuggestList([])
  }

  useEffect(() => {
    if (!detailData.length) return
    const target = detailData.sort((a, b) => a.time - b.time)[0]
    setDisplayName(target.display_name)
    setDisplayColor(target.color)
    setDisplayIcon(target.icon)
    setSuggestList([])
  }, [isDisplay, detailData])

  useEffect(() => {
    setDetailData(details)
  }, [details])

  useEffect(() => {
    const container = suggestRef.current
    container?.addEventListener("scroll", onScroll)
  }, [suggestList])

  return (
    <Modal
      title="アプリケーション編集"
      show={isDisplay}
      wrapperClass="edit_target_application"
      closeCallback={closeModal}
      closeable
    >
      <>
        <div
          className="item"
          style={{ position: "relative" }}
          onMouseLeave={() => setSuggestList([])}
        >
          <div className="title">
            アプリケーション名
          </div>
          <div className="detail equal_space row_1">
            <input
              type="text"
              value={displayName}
              onChange={(e) => setName(e.target.value)}
              onFocus={() => search(displayName)}
              onKeyPress={(e) => e.key === "Enter" && setSuggestList([])}
            />
          </div>
          {
            !displayName.length && (
              <div className="error">
                アプリケーション名を入力してください
              </div>
            )
          }
          {
            !!suggestList.length && (
              <div className="suggest_list" ref={suggestRef}>
                {
                  slicedData.filter((_w, i) => i <= sliceNumber).flat().map((v) => (
                    <div
                      role="none"
                      onClick={() => selectSuggest(v.display_name)}
                      className="list_detail"
                    >
                      {
                        v.icon.split(' ').length === 2 && (
                          <FontAwesomeIcon
                            icon={[v.icon.split(' ')[0] as IconPrefix, v.icon.split(' ')[1] as IconName]}
                            style={{
                              color: v.color,
                              fontSize: "12px",
                            }}
                            fixedWidth
                          />
                        )
                      }
                      {v.display_name}
                    </div>
                  ))
                }
                {
                  slicedData.length - 1 > sliceNumber && (
                    <>
                      <LoadingSquare>
                        <>
                          アプリを表示中…
                        </>
                      </LoadingSquare>
                    </>
                  )
                }
              </div>
            )
          }
        </div>
        <div className="item">
          <div className="title">
            アイコン・カラー
            {
              !detailData.map((v) => v.display_name).includes(displayName) && appList.map((v) => v.display_name).includes(displayName) && (
                <div
                  style={{
                    border: "solid 1px #008800",
                    borderRadius: "4px",
                    fontWeight: "normal",
                    padding: "2px 8px",
                    backgroundColor: "#008800",
                    color: "#FFFFFF",
                    fontSize: "11px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <FontAwesomeIcon
                    icon={['fas', 'lightbulb']}
                    style={{
                      color: "#fff016",
                      fontSize: "12px",
                    }}
                  />
                  同名のアプリケーションが存在するため、アイコンとカラーは自動設定されます
                </div>
              )
            }
          </div>
          <div
            className="detail equal_space row_1"
            style={{
              justifyContent: "left",
              gap: "8px",
            }}
          >
            <AddIconModule
              color={displayColor}
              icon={displayIcon}
              changeCallback={(icon) => setDisplayIcon(icon)}
              disabled={isDuplicateApp}
            />
            <label
              htmlFor="color"
              style={{
                border: "solid 1px #CCCCCC",
                borderRadius: "4px",
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: isDuplicateApp ? "#EEEEEE" : "#FFFFFF",
                cursor: isDuplicateApp ? "not-allowed" : "pointer",
                color: isDuplicateApp ? "#999999" : "#333333",
              }}
            >
              <input
                id="color"
                type="color"
                value={displayColor}
                onChange={(e) => setDisplayColor(e.target.value)}
                disabled={isDuplicateApp}
                style={{
                  cursor: isDuplicateApp ? "not-allowed" : "pointer",
                }}
              />
              {displayColor}
              <FontAwesomeIcon
                icon={['fas', 'angle-down']}
                style={{
                  color: isDuplicateApp ? "#999999" : "#666666",
                  fontSize: "16px",
                }}
              />
            </label>
          </div>
        </div>
        <div className="item">
          <div className="title">
            対象プロセス・ドメイン名
          </div>
          <div
            style={{
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              padding: "16px",
              display: "flex",
              flexFlow: "column",
              gap: "8px",
              backgroundColor: "#FAFAFA",
            }}
          >
            {
              detailData.map((v) => v.company_service_names).flat().map((w) => (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: w.service_type === "application" ? "#ad0505" : "#067ecd",
                      color: "#FFFFFF",
                      borderRadius: "4px",
                      fontSize: "10px",
                      padding: "1px 4px",
                      fontWeight: "initial",
                    }}
                  >
                    {w.service_type === "application" ? "プロセス名" : "ドメイン名"}
                  </div>
                  {w.raw_name}
                  {
                    detailData.length === 1 && detailData.map((v) => v.company_service_names).flat().length !== 1 && (
                      <button
                        type="button"
                        style={{
                          border: "solid 1px #CCCCCC",
                          borderRadius: "4px",
                          padding: "2px 4px",
                          fontSize: "10px",
                          backgroundColor: "#FFFFFF",
                        }}
                        onClick={() => separateTargetApplication(w)}
                      >
                        別のアプリケーション名にする
                      </button>
                    )
                  }
                </div>
              ))
            }
          </div>
        </div>
        {
          !!windowTitles.length && detailData.map((v) => v.company_service_names).flat().length === 1 && (
            <div className="item">
              <div className="title">
                対象プロセス・ドメイン名で取得したタイトルのサンプル一覧
              </div>
              <div
                style={{
                  border: "solid 1px #CCCCCC",
                  borderRadius: "4px",
                  padding: "16px",
                  display: "flex",
                  flexFlow: "column",
                  gap: "8px",
                  backgroundColor: "#EEEEEE",
                }}
              >
                {
                  windowTitles.map((v) => (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {v}
                    </div>
                  ))
                }
              </div>
            </div>
          )
        }
      </>
      <>
        <button
          type="button"
          className="button cancel"
          onClick={closeModal}
        >
          キャンセル
        </button>
        <button
          type="button"
          className="button"
          onClick={submitTargetApplication}
          disabled={!displayName}
        >
          更新
        </button>
      </>
    </Modal>
  )
}

export default EditTargetApplicationModal
